import qs from 'qs';

import { Button, PetitionCard, SegmentedControl, SegmentedControlOption, Spinner, Text } from '@change/ds-core-ui';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';

import { PromotedImpressionTracker } from 'src/app/shared/components/promotionTracking';
import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { useFilterablePetitionsSection } from './hooks/useFilterablePetitionsSection';

// eslint-disable-next-line max-lines-per-function
export const FilterablePetitionsSection = () => {
	const {
		data: { headingText, hasNextPage, items, nextPageLoading, selectedSort, displayed },
		actions: { loadMore, setSelectedSort },
	} = useFilterablePetitionsSection();
	const { translate, translatePlural, getCountry } = useI18n();

	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	if (!displayed) return null;

	const trendingLabel = translate('corgi.topic_page.petition_section.trending');
	const recentLabel = translate('corgi.topic_page.petition_section.most_recent');

	return (
		<div className="flex flex-col" data-qa="explore-petitions-container">
			<Text
				as="h2"
				variant="heading"
				size="small"
				className="text-typography-strong mb-6"
				data-qa="explore-petitions-header"
			>
				{headingText}
			</Text>

			<SegmentedControl
				className="mb-6 self-start"
				onChange={(id) => setSelectedSort(id === 'trending' ? 'trending' : 'recent')}
				defaultSelected={selectedSort}
				data-qa="explore-petitions-segmented-control"
			>
				<SegmentedControlOption id="trending" ariaLabel={trendingLabel}>
					{translate('corgi.topic_page.petition_section.trending')}
				</SegmentedControlOption>
				<SegmentedControlOption id="recent" ariaLabel={recentLabel}>
					{translate('corgi.topic_page.petition_section.most_recent')}
				</SegmentedControlOption>
			</SegmentedControl>

			<div ref={containerRef} className="flex flex-col items-center gap-6">
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{items.map(({ promotionToken, node: petition }, index) => {
						return (
							<div className="flex" key={petition.id}>
								{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
									<VisuallyHidden role="alert">
										<TranslatePlural
											value="corgi.topic_page.petition_section.fetched_more"
											count={items.length - lastEvent.previousItemsCount}
											replacements={{ count: items.length - lastEvent.previousItemsCount }}
										/>
									</VisuallyHidden>
								)}

								{promotionToken && (
									<PromotedImpressionTracker
										petitionId={petition.id}
										placement="topics_page"
										promotionToken={promotionToken}
									/>
								)}

								<PetitionCard
									ref={getListItemRef(index)}
									imageUrl={petition.photo?.petitionMedium.url}
									location={
										petition.relevantLocationLocalizedName || getCountry(petition.country.countryCode)?.name || ''
									}
									className="w-full"
									petitionId={petition.id}
									petitionLinkUrl={`/p/${encodeURIComponent(petition.slug)}${qs.stringify({ source_location: 'topics_page', ...(promotionToken && { pt: promotionToken }) }, { addQueryPrefix: true })}`}
									signaturesLabel={translatePlural(
										'corgi.components.petition_card.signatures',
										petition.signatureState.signatureCount.displayed,
										{
											countLocalized: petition.signatureState.signatureCount.displayedLocalized,
										},
									)}
									title={petition.ask}
									data-qa="explore-petitions-petition-card"
								/>
							</div>
						);
					})}
				</div>

				{nextPageLoading && (
					<>
						<VisuallyHidden role="alert">
							<Translate value="corgi.topic_page.petition_section.fetching_more" />
						</VisuallyHidden>
						<Spinner className="mx-auto mt-6" ref={loadingRef} />
					</>
				)}

				{!nextPageLoading && hasNextPage && (
					<Button
						size="small"
						variant="secondary"
						data-qa="explore-petitions-show-more-button"
						className="max-w-[348px] max-md:w-full!"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: items.length });
							loadMore();
						}}
						ref={loadMoreRef}
					>
						<Translate value="corgi.topic_page.petition_section.show_more" />
					</Button>
				)}
			</div>
		</div>
	);
};
